import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import Background from '../components/Background';
import { retryWithBackoff } from '../utils/retry';
import { getErrorMessage } from '../utils/errorHandler';
import logger from '../utils/logger';

interface ChartCalculationData {
  birth_date: string;
  birth_time: string | null;
  birth_city: string;
  birth_country: string;
  birth_timezone?: string;
  birth_latitude?: number;
  birth_longitude?: number;
}

// Fun, quirky astrology facts to rotate through
const ASTROLOGY_FACTS = [
  "Did you know? Neptune takes 165 years to orbit the Sun—longer than most humans live!",
  "Venus rotates backwards compared to most planets. She does things her own way.",
  "Mercury retrograde happens 3-4 times a year. It's not as rare as you think!",
  "The Moon affects ocean tides, but did you know it also affects your emotional tides?",
  "Saturn's rings are made of ice and rock particles. Your Saturn return? That's made of life lessons.",
  "Jupiter is so massive, it could fit all other planets inside it. Talk about expansion!",
  "Mars is red because of iron oxide (rust) on its surface. No wonder it's the planet of action!",
  "Uranus rotates on its side—literally. It's the rebel of the solar system.",
  "The Sun contains 99.86% of the solar system's mass. No wonder it's the center of everything!",
  "Pluto was demoted to a dwarf planet in 2006, but astrologers still use it. We don't give up easily!",
  "Your rising sign changes every 2 hours. That's why birth time matters so much!",
  "There are 12 houses in astrology, but only 10 planets. Some houses get extra attention.",
  "The word 'disaster' comes from 'dis-aster' meaning 'bad star' in Greek. Ancient wisdom!",
  "Retrograde planets appear to move backward from Earth's perspective. It's all about perspective!",
  "The zodiac signs are based on constellations, but they've shifted over 2,000 years. Nothing stays the same!"
];

const CalculatingChart: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshOnboardingStatus } = useAuth();
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Preparing your cosmic blueprint...');
  const [currentFactIndex, setCurrentFactIndex] = useState(0);

  useEffect(() => {
    logger.componentMount('CalculatingChart', {});

    // Get chart data from location state (passed from onboarding)
    const chartData = location.state as ChartCalculationData | null;

    if (!chartData) {
      logger.warn('CalculatingChart', 'No chart data provided, redirecting to onboarding');
      navigate('/onboarding', { replace: true });
      return;
    }

    const calculateChart = async () => {
      try {
        setStatus('Aligning stars...');
        logger.info('CalculatingChart', 'Starting chart calculation', { chartData: { ...chartData, birth_city: chartData.birth_city } });

        // Calculate birth chart
        logger.apiCall('CalculatingChart', 'POST', '/chart/calculate', chartData);
        const chartResponse = await retryWithBackoff(() => api.post('/chart/calculate', chartData));
        logger.apiResponse('CalculatingChart', 'POST', '/chart/calculate', 200, { hasData: !!chartResponse.data });

        setStatus('Finalizing...');

        // Refresh onboarding status
        try {
          await refreshOnboardingStatus();
          logger.info('CalculatingChart', 'Onboarding status refreshed');
        } catch (err) {
          logger.error('CalculatingChart', 'Failed to refresh onboarding status', err);
        }

        // No artificial delay needed

        logger.info('CalculatingChart', 'Chart calculation completed, navigating to user page');
        navigate('/user', { replace: true });
      } catch (err: any) {
        const errorMessage = getErrorMessage(err);
        logger.error('CalculatingChart', 'Chart calculation error', err, {
          errorMessage,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          url: err.config?.url
        });

        if (errorMessage.includes('timezone') || errorMessage.includes('Could not determine timezone')) {
          setError('We couldn\'t determine the timezone for your birth location. Please go back and select your timezone manually.');
        } else {
          setError(`Failed to calculate your birth chart: ${errorMessage}`);
        }
      }
    };

    calculateChart();

    return () => {
      logger.componentUnmount('CalculatingChart');
    };
  }, [navigate, location.state, refreshOnboardingStatus]);

  // Rotate through astrology facts every 4 seconds
  useEffect(() => {
    if (error) return; // Don't rotate facts if there's an error

    const factInterval = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % ASTROLOGY_FACTS.length);
    }, 4000);

    return () => clearInterval(factInterval);
  }, [error]);

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 sm:px-6" style={{ backgroundColor: '#050508' }}>
      <Background />
      <div className="max-w-2xl w-full relative z-10 animate-fade-in text-center">
        {error ? (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full border-2 border-red-500/30 flex items-center justify-center">
              <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gradient-gold mb-4 tracking-luxury">
                Calculation Error
              </h2>
              <p className="text-text-secondary font-elegant text-base sm:text-lg mb-6">
                {error}
              </p>
              <button
                onClick={() => navigate('/onboarding', { replace: true })}
                className="glass-grain border border-cosmic-gold/40 hover:border-cosmic-gold/60 bg-space-dark/40 hover:bg-space-dark/60 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] px-6 py-3 rounded-xl text-cosmic-gold font-elegant"
              >
                Go Back to Onboarding
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Animated Planet Icon */}
            <div className="relative w-24 h-24 mx-auto">
              <div
                className="absolute inset-0 rounded-full border-2 animate-spin"
                style={{
                  borderColor: 'rgba(255, 184, 79, 0.3)',
                  borderTopColor: '#FFB84F',
                  animationDuration: '2s'
                }}
              />
              <div
                className="absolute inset-2 rounded-full border-2 animate-spin"
                style={{
                  borderColor: 'rgba(255, 184, 79, 0.2)',
                  borderTopColor: '#FFB84F',
                  animationDuration: '3s',
                  animationDirection: 'reverse'
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 184, 79, 0.2) 0%, rgba(255, 184, 79, 0.1) 100%)',
                    border: '2px solid rgba(255, 184, 79, 0.4)',
                    boxShadow: '0 0 20px rgba(255, 184, 79, 0.3)'
                  }}
                >
                  <span className="text-cosmic-gold">☉</span>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gradient-gold mb-4 tracking-luxury">
                Calculating Your Chart
              </h2>
              <p className="text-text-secondary font-elegant text-base sm:text-lg mb-2">
                {status}
              </p>
              <p className="text-text-tertiary font-elegant text-sm sm:text-base italic mb-6 mt-4">
                This might take a while...
              </p>
              <div className="flex items-center justify-center gap-2 mb-8">
                <div className="w-2 h-2 rounded-full bg-cosmic-gold animate-pulse" style={{ animationDelay: '0s' }} />
                <div className="w-2 h-2 rounded-full bg-cosmic-gold animate-pulse" style={{ animationDelay: '0.2s' }} />
                <div className="w-2 h-2 rounded-full bg-cosmic-gold animate-pulse" style={{ animationDelay: '0.4s' }} />
              </div>

              {/* Rotating Astrology Facts */}
              <div className="mt-8 max-w-lg mx-auto">
                <div
                  key={currentFactIndex}
                  className="text-text-secondary/80 font-elegant text-sm sm:text-base italic animate-fade-in"
                  style={{
                    minHeight: '3rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <p className="text-center px-4">
                    {ASTROLOGY_FACTS[currentFactIndex]}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculatingChart;

